/**
 * One blog cover, delivered once, used on both surfaces.
 *
 * ── WHY A COMPONENT AND NOT TWO <img> TAGS ──────────────────────────────────
 *
 * The card on /blog/ and the hero on a post page had the same eight attributes
 * written out twice, and they had already drifted: one lazy, one eager, one
 * rounded, one not. A `<picture>` with three sources and a sizes string is more
 * than twice as much to keep in agreement, and the two surfaces genuinely
 * differ in exactly one thing -- which of them holds the LCP element. So that
 * is the prop, and everything else is shared.
 *
 * ── WHAT THE NUMBERS ARE ────────────────────────────────────────────────────
 *
 * Measured, not guessed. Cards render 284x149 CSS px at a 390 viewport and
 * 378x198 at 1440 (screenshotted 2026-09-01); the detail hero is capped at
 * 880px inside a px-6 gutter. So:
 *
 *   card    (min-width: 768px) 380px, 75vw
 *   detail  (min-width: 928px) 880px, calc(100vw - 48px)
 *
 * The 400w variant covers a phone card at 1dpr, 800w covers the same card at
 * 2dpr and the detail page at 1dpr, 1200w covers the detail page at 2dpr.
 *
 * ── THE PNG IS THE FALLBACK AND IT IS NOT DEAD WEIGHT ───────────────────────
 *
 * WebP is ~95% supported, so the `<img src>` PNG is the remainder -- and it is
 * also the URL `ogImage` hands to crawlers, several of which do not take WebP.
 * Deleting the PNGs would save repo weight and break link previews.
 *
 * ── LCP ─────────────────────────────────────────────────────────────────────
 *
 * `priority` sets loading=eager AND fetchpriority=high. eager alone only means
 * "do not defer"; it does not raise the request's place in the queue, and the
 * detail cover sits directly under the h1, so it is almost always the LCP
 * element. Everything else gets loading=lazy and decoding=async, which keeps
 * decode off the main thread while a reader scrolls the index.
 *
 * width/height stay on the <img> alongside the CSS aspect-ratio: both were
 * already there, CLS was never the defect here, and removing either to tidy up
 * would introduce one.
 */
export function CoverImage({ src, alt, priority = false, className = '', style = {} }) {
  // '/blog/foo.png' -> '/blog/foo'. The variants are derived by
  // scripts/gen_cover_variants.py and pinned by check-cover-budget.mjs.
  const base = src.replace(/\.png$/, '');
  const sizes = priority
    ? '(min-width: 928px) 880px, calc(100vw - 48px)'
    : '(min-width: 768px) 380px, 75vw';

  return (
    <picture>
      <source
        type="image/webp"
        srcSet={`${base}-400.webp 400w, ${base}-800.webp 800w, ${base}.webp 1200w`}
        sizes={sizes}
      />
      <img
        src={src}
        alt={alt}
        width={1200}
        height={630}
        loading={priority ? 'eager' : 'lazy'}
        {...(priority ? { fetchPriority: 'high' } : { decoding: 'async' })}
        className={className}
        style={{ aspectRatio: '1200 / 630', objectFit: 'cover', ...style }}
      />
    </picture>
  );
}

export default CoverImage;
